/**
 * Provider-agnostic refund status — the normalised, mapper-output state our
 * code branches on after PayPal / Stripe report a refund outcome. Three values:
 *
 *   - `COMPLETED` — provider acknowledges money returned to buyer.
 *   - `PENDING`   — provider has not finished processing the refund yet.
 *   - `FAILED`    — provider definitively rejected / failed the refund.
 *
 * Why this is in `@kraahl/contract` (not a local TS literal under `lib/payments`):
 *
 * `RefundStatus` is the discriminator for downstream UI consumers (admin
 * dashboards, buyer order pages, refund-status emails). Three layers branch on
 * it: PayPal mapper, Stripe mapper, and `applyRefundTerminalStatus` (which
 * persists to `seller_orders.refund_status`). Pre-Sibling-C the type was a
 * hand-rolled `"COMPLETED" | "PENDING" | "FAILED"` literal in
 * `lib/payments/types.ts` — no runtime Zod boundary, no schema-pinned
 * exhaustiveness probe across providers. If PayPal / Stripe ever adds a fourth
 * state and we extend the union, downstream UI consumers silently miss the new
 * variant until somebody manually audits every switch.
 *
 * The fix: one canonical `z.enum` here, imported by every mapper and consumer.
 * The bidirectional pin below makes the `REFUND_STATUSES` array and the
 * `RefundStatus` type provably equivalent — adding a value to one without the
 * other fails `tsc`. The schema is the runtime boundary; `RefundStatusSchema.
 * safeParse(...)` at the PayPal mapper guards against drift between the
 * mapper's hardcoded literal branches and the canonical enum.
 *
 * Note on scope: this is the **mapper-output** type, NOT the database column
 * type. `seller_orders.refund_status` is a wider domain (PayPal webhook
 * handlers also write `"REVERSED"` / `"CANCELLED"` straight through via
 * `applyRefundTerminalStatus`). The provider-agnostic 3-value enum is what
 * downstream "did the refund succeed?" UI branches on; the wider DB column is
 * what the PayPal webhook's terminal-failure email path uses.
 *
 * @see lib/payments/refund-status.ts — mapPaypalRefundStatus / mapStripeRefundStatus
 * @see lib/payments/types.ts — RefundResult.status field
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-C
 * @see .tondocs/paypal/REFUND_WEBHOOK.md — error-path policy
 */

import { z } from "zod";

/**
 * Exhaustive allowlist of every provider-agnostic refund status. The mapper
 * functions (`mapPaypalRefundStatus`, `mapStripeRefundStatus`) project provider
 * strings onto this set; downstream consumers (UI, emails) branch on it with
 * `default: unreachable(x: never)` exhaustiveness.
 *
 * Adding a value here triggers compile breaks across:
 *   - `RefundStatusSchema` (forced via the bidirectional pin below)
 *   - `STRIPE_COVERAGE` / `PAYPAL_COVERAGE` tables in
 *     `tests/unit/payments-refund-status-coverage.test.ts`
 *     (Record-keyed on the providers' documented sets — RefundStatus is the
 *     value side)
 *   - `tests/unit/refund-status-roundtrip.test.ts` SAMPLES record (added by
 *     Sibling-C as the canonical "did anyone forget a mapper update?" probe)
 *
 * See `.tondocs/TEST_STRATEGY.md` §3 (exhaustiveness probe doctrine).
 */
export const REFUND_STATUSES = ["COMPLETED", "PENDING", "FAILED"] as const;

/**
 * Runtime boundary parser. Use at every provider mapper that produces a
 * `RefundStatus`.
 *
 * Schema-first declaration: `RefundStatus` is derived from this schema
 * (`z.infer<typeof RefundStatusSchema>`), so a bidirectional `pinned()`
 * wrapper would create a circular reference (the helper's `T` parameter
 * resolves through `typeof RefundStatusSchema`). Drift detection lives
 * instead on the `_AssertKindsCoverUnion` / `_AssertNoExtraKinds` pair
 * below — those pin `REFUND_STATUSES` (the array) against `RefundStatus`
 * (the union), so a fourth status added to one but not the other breaks
 * compile here before any downstream code is touched. See pin.ts header.
 */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: RefundStatus is z.infer<typeof RefundStatusSchema>; drift is caught by the array↔union pin below.
export const RefundStatusSchema = z.enum(REFUND_STATUSES);

/** Canonical provider-agnostic refund status. Single source of truth. */
export type RefundStatus = z.infer<typeof RefundStatusSchema>;

// Compile-time exhaustiveness pin: the `REFUND_STATUSES` array must exactly
// cover the `RefundStatus` union. Same pattern as
// `CHECKOUT_ITEM_CONTEXT_KINDS` in checkout-intent.ts — adding a value to the
// array without the union, or vice versa, fails the build. This is a distinct
// invariant from the schema↔interface `pinned()` wrappers used elsewhere in
// this package (here `RefundStatus` is inferred from the schema, so this
// array↔union pin is the meaningful drift gate).
type _AssertKindsCoverUnion =
  (typeof REFUND_STATUSES)[number] extends RefundStatus ? true : never;
type _AssertNoExtraKinds =
  RefundStatus extends (typeof REFUND_STATUSES)[number] ? true : never;

const _pinKindsCoverUnion: _AssertKindsCoverUnion = true;
const _pinNoExtraKinds: _AssertNoExtraKinds = true;
void _pinKindsCoverUnion;
void _pinNoExtraKinds;
